/**
 * 贪心分配助手：策略层共用（设计文档 3.4）。
 *
 * 关键：在单次 allocate() 批次内，按已分配计数累计「虚拟载客」，
 * 使后续呼叫不再挤向同一辆车 —— 避免把远超容量的呼叫堆给一辆车
 * 导致其在多个楼层间反复折返（性能爆炸/死循环）。
 * 跨批次则由内核更新后的真实载客数自然兜底。
 */
import type { Assignment, CallView, CarView } from './strategy-api';

/**
 * @param cost 代价函数：(车, 呼叫, 本批次已派给该车的数量) => 数值，越小越优
 */
export function greedyAssign(
  cars: readonly CarView[],
  calls: readonly CallView[],
  cost: (car: CarView, call: CallView, assignedToCar: number) => number,
): Assignment[] {
  const assigned = new Map<string, number>();
  const out: Assignment[] = [];

  for (const call of calls) {
    let bestCar: CarView | null = null;
    let bestCost = 0;
    let bestId = '';
    let haveBest = false;

    for (const car of cars) {
      const extra = assigned.get(car.id) ?? 0;
      // 已承诺载客 = 轿内 + 已指派待上梯 + 本批次虚拟；达容量则跳过，避免单梯过载
      if (car.load + car.pending + extra >= car.capacity) continue;
      const c = cost(car, call, extra);
      if (!haveBest || c < bestCost || (c === bestCost && car.id < bestId)) {
        bestCar = car;
        bestCost = c;
        bestId = car.id;
        haveBest = true;
      }
    }

    if (bestCar) {
      out.push({ callId: call.id, carId: bestCar.id });
      assigned.set(bestCar.id, (assigned.get(bestCar.id) ?? 0) + 1);
    }
  }

  return out;
}
